/**
 * Custom React Hooks for API Operations
 * Provides reusable hooks with loading states, error handling, and caching
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../services/api';
import { handleError, logError, isRetryableError, getRetryDelay } from '../utils/errorHandler';
import type { AnalysisResult } from '../types/api';

// TypeScript interfaces
interface ApiError {
  message: string;
  code?: string | number;
  status?: number;
}

interface UploadResult {
  upload_id: string;
  filename: string;
  status: string;
}

interface UseApiOptions {
  immediate?: boolean;
  retries?: number;
}

interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  lastFetch: number | null;
  execute: (...args: any[]) => Promise<T>;
  reset: () => void;
  refresh: () => Promise<T>;
  isStale: boolean;
}

interface UseFileUploadOptions {
  onSuccess?: (result: UploadResult) => void;
  onError?: (error: ApiError) => void;
  maxRetries?: number;
}

interface UseFileUploadResult {
  upload: (fileOrFiles: File | File[], powerUnit?: 'watt' | 'kilowatt') => Promise<UploadResult>;
  uploading: boolean;
  progress: number;
  uploadResult: UploadResult | null;
  error: ApiError | null;
  reset: () => void;
}

interface UseAnalysisResult {
  analysisResult: AnalysisResult | null;
  analyzing: boolean;
  error: ApiError | null;
  startAnalysis: () => Promise<void>;
  getResults: () => Promise<AnalysisResult>;
}

/**
 * Generic API hook with loading states and error handling
 */
export function useApi<T>(
  apiCall: (...args: any[]) => Promise<T>,
  dependencies: React.DependencyList = [],
  options: UseApiOptions = {}
): UseApiResult<T> {
  const {
    immediate = false,
    retries = 2,
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [lastFetch, setLastFetch] = useState<number | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const retryCountRef = useRef<number>(0);
  const immediateExecutedRef = useRef<boolean>(false);
  const apiCallRef = useRef(apiCall);

  // Update apiCallRef when apiCall changes
  useEffect(() => {
    apiCallRef.current = apiCall;
  }, [apiCall]);

  // Execute API call with retry logic
  const execute = useCallback(async (...args: any[]): Promise<T> => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setLoading(true);
    setError(null);

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const result = await apiCallRef.current(...args);

      setData(result);
      setLastFetch(Date.now());
      retryCountRef.current = 0;

      return result;
    } catch (err) {
      const formattedError = handleError(err as any) as ApiError;

      // Log error for monitoring
      logError(formattedError, {
        apiCall: apiCallRef.current.name,
        args,
        retryCount: retryCountRef.current
      } as any);

      // Check if we should retry
      if (isRetryableError(formattedError) && retryCountRef.current < retries) {
        retryCountRef.current++;
        const delay = getRetryDelay(formattedError, retryCountRef.current);

        console.log(`Retrying API call in ${delay}ms (attempt ${retryCountRef.current}/${retries})`);

        setTimeout(() => {
          execute(...args);
        }, delay);

        return undefined as any;
      }

      setError(formattedError);
      throw formattedError;
    } finally {
      setLoading(false);
    }
  }, [retries]);

  // Store execute in ref to avoid dependency issues
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  }, [execute]);

  // Auto-execute on mount if immediate is true - ONLY ONCE
  useEffect(() => {
    if (immediate && !immediateExecutedRef.current) {
      immediateExecutedRef.current = true;
      executeRef.current();
    }

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [immediate]);

  // Reset function
  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
    setLastFetch(null);
    retryCountRef.current = 0;
  }, []);

  // Refresh function
  const refresh = useCallback(() => {
    return executeRef.current();
  }, []);

  return {
    data,
    loading,
    error,
    lastFetch,
    execute,
    reset,
    refresh,
    isStale: false, // No caching, so never stale
  };
}

/**
 * Hook for file upload with progress tracking (single or multiple files)
 */
export function useFileUpload(options: UseFileUploadOptions = {}): UseFileUploadResult {
  const { onSuccess, onError } = options;

  const [uploading, setUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const upload = useCallback(async (fileOrFiles: File | File[], powerUnit: 'watt' | 'kilowatt' = 'watt'): Promise<UploadResult> => {
    // Handle both single file and multiple files
    const isSingleFile = !Array.isArray(fileOrFiles);
    const files = isSingleFile ? [fileOrFiles as File] : fileOrFiles as File[];

    if (!files || files.length === 0) {
      const fileError = handleError({ message: 'Keine Datei ausgewählt' }) as ApiError;
      setError(fileError);
      onError?.(fileError);
      throw fileError;
    }

    setUploading(true);
    setProgress(0);
    setError(null);
    setUploadResult(null);

    try {
      let result: UploadResult;

      if (isSingleFile) {
        // Single file upload with power unit (Meeting 2025-10-03)
        result = await (apiClient as any).uploadFile(files[0], (progressPercent: number) => {
          setProgress(progressPercent);
        }, powerUnit);
      } else {
        // Multiple files upload with power unit (Meeting 2025-10-03)
        result = await (apiClient as any).uploadMultipleFiles(files, (progressPercent: number) => {
          setProgress(progressPercent);
        }, powerUnit);
      }

      setUploadResult(result);
      onSuccess?.(result);
      return result;
    } catch (err) {
      const formattedError = handleError(err as any) as ApiError;
      setError(formattedError);
      const logData = isSingleFile
        ? { filename: files[0].name, fileSize: files[0].size }
        : { fileCount: files.length, totalSize: files.reduce((sum, f) => sum + f.size, 0) };
      logError(formattedError, logData as any);
      onError?.(formattedError);
      throw formattedError;
    } finally {
      setUploading(false);
    }
  }, [onSuccess, onError]);

  const reset = useCallback(() => {
    setUploading(false);
    setProgress(0);
    setUploadResult(null);
    setError(null);
  }, []);

  return {
    upload,
    uploading,
    progress,
    uploadResult,
    error,
    reset,
  };
}

/**
 * Hook for analysis operations
 */
export function useAnalysis(uploadId: string | undefined): UseAnalysisResult {
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [analyzing, setAnalyzing] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);
  const uploadIdRef = useRef(uploadId);

  // Update uploadIdRef when uploadId changes
  useEffect(() => {
    uploadIdRef.current = uploadId;
  }, [uploadId]);

  const startAnalysis = useCallback(async (): Promise<void> => {
    const currentUploadId = uploadIdRef.current;
    if (!currentUploadId) return;

    setAnalyzing(true);
    setError(null);

    try {
      // Start analysis - our backend returns the result directly
      const result = await apiClient.startAnalysis(currentUploadId);

      // Check if we got a completed result directly
      if (result.status === 'completed') {
        setAnalysisResult(result);
        setAnalyzing(false);
        return;
      } else if (result.status === 'failed') {
        throw new Error(result.error || 'Analysis failed');
      }

      // If still processing, poll for results
      if (result.status === 'processing') {
        const pollResults = async () => {
          try {
            const polledResult = await apiClient.getAnalysisResults(currentUploadId);

            if (polledResult.status === 'completed') {
              setAnalysisResult(polledResult);
              setAnalyzing(false);
              return;
            } else if (polledResult.status === 'failed') {
              throw new Error(polledResult.error || 'Analysis failed');
            }

            // Continue polling if still processing
            if (polledResult.status === 'processing') {
              setTimeout(pollResults, 2000); // Poll every 2 seconds
            }
          } catch (err) {
            const formattedError = handleError(err as any) as ApiError;
            setError(formattedError);
            setAnalyzing(false);
            logError(formattedError, { uploadId: currentUploadId, operation: 'pollResults' } as any);
          }
        };

        // Start polling
        setTimeout(pollResults, 1000);
      }

    } catch (err) {
      const formattedError = handleError(err as any) as ApiError;
      setError(formattedError);
      setAnalyzing(false);
      logError(formattedError, { uploadId: currentUploadId, operation: 'startAnalysis' } as any);
    }
  }, []); // Empty dependency array - use uploadIdRef instead

  const getResults = useCallback(async (): Promise<AnalysisResult> => {
    const currentUploadId = uploadIdRef.current;
    if (!currentUploadId) throw new Error('Upload ID is required');

    try {
      const result = await apiClient.getAnalysisResults(currentUploadId);
      setAnalysisResult(result);
      return result;
    } catch (err) {
      const formattedError = handleError(err as any) as ApiError;
      setError(formattedError);
      logError(formattedError, { uploadId: currentUploadId, operation: 'getResults' } as any);
      throw formattedError;
    }
  }, []); // Empty dependency array - use uploadIdRef instead

  return {
    analysisResult,
    analyzing,
    error,
    startAnalysis,
    getResults,
  };
}

/**
 * Hook for chart data - NO CACHING
 * Chart data is fetched fresh on each request to avoid stale data issues.
 */
export function useChartData(uploadId: string | undefined, chartType: string = 'all') {
  const [chartData, setChartData] = useState<any>(null);
  const [loadingCharts, setLoadingCharts] = useState<boolean>(false);
  const [chartError, setChartError] = useState<ApiError | null>(null);
  const hasLoadedRef = useRef<string | null>(null);
  const isLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!uploadId) {
      setChartData(null);
      hasLoadedRef.current = null;
      return;
    }

    // Prevent loading the same data multiple times
    const cacheKey = `${uploadId}-${chartType}`;
    if (hasLoadedRef.current === cacheKey) {
      console.log(`🔵 useChartData: Already loaded ${cacheKey}, skipping`);
      return;
    }

    // Prevent concurrent loads
    if (isLoadingRef.current) {
      console.log(`🔵 useChartData: Already loading, skipping duplicate request`);
      return;
    }

    let isCancelled = false;

    const loadCharts = async () => {
      isLoadingRef.current = true;
      setLoadingCharts(true);
      setChartError(null);

      try {
        console.log(`🔵 useChartData: Loading charts for uploadId=${uploadId}, type=${chartType}`);
        const data = await apiClient.getChartData(uploadId, chartType);

        if (!isCancelled) {
          setChartData(data);
          hasLoadedRef.current = cacheKey;
          console.log(`🔵 useChartData: Successfully loaded charts`);
        }
      } catch (err) {
        if (!isCancelled) {
          const formattedError = handleError(err as any) as ApiError;
          setChartError(formattedError);
          logError(formattedError, { uploadId, chartType } as any);
        }
      } finally {
        if (!isCancelled) {
          setLoadingCharts(false);
          isLoadingRef.current = false;
        }
      }
    };

    loadCharts();

    return () => {
      isCancelled = true;
      isLoadingRef.current = false;
    };
  }, [uploadId, chartType]);

  const loadCharts = useCallback(() => {
    // Trigger reload by clearing data first
    setChartData(null);
  }, []);

  const refreshCharts = useCallback(async () => {
    if (!uploadId) return null;

    setLoadingCharts(true);
    setChartError(null);

    try {
      const data = await apiClient.getChartData(uploadId, chartType);
      setChartData(data);
      return data;
    } catch (err) {
      const formattedError = handleError(err as any) as ApiError;
      setChartError(formattedError);
      throw formattedError;
    } finally {
      setLoadingCharts(false);
    }
  }, [uploadId, chartType]);

  return {
    chartData,
    loadingCharts,
    chartError,
    loadCharts,
    refreshCharts,
  };
}

/**
 * Hook for health check with periodic polling
 */
export function useHealthCheck(pollInterval: number = 30000) {
  const [isHealthy, setIsHealthy] = useState<boolean>(true);
  const [lastCheck, setLastCheck] = useState<number | null>(null);

  const {
    data: healthData,
    loading: checkingHealth,
    error: healthError,
    execute: checkHealth,
  } = useApi(
    () => apiClient.healthCheck(),
    [],
    { immediate: true }
  );

  useEffect(() => {
    if (healthData) {
      setIsHealthy(healthData.status === 'healthy');
      setLastCheck(Date.now());
    }
  }, [healthData]);

  useEffect(() => {
    if (healthError) {
      setIsHealthy(false);
    }
  }, [healthError]);

  // Periodic health checking
  useEffect(() => {
    if (!pollInterval) return;

    const interval = setInterval(() => {
      checkHealth();
    }, pollInterval);

    return () => clearInterval(interval);
  }, [pollInterval, checkHealth]);

  return {
    isHealthy,
    healthData,
    healthError,
    checkingHealth,
    lastCheck,
    checkHealth,
  };
}

/**
 * Hook for submitting lead data
 */
export function useLeadSubmission() {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);

  const submitLead = useCallback(async (leadData: any) => {
    setSubmitting(true);
    setError(null);

    try {
      const result = await apiClient.submitLead(leadData);
      setSubmitted(true);
      return result;
    } catch (err) {
      const formattedError = handleError(err as any) as ApiError;
      setError(formattedError);
      logError(formattedError, { operation: 'submitLead' } as any);
      throw formattedError;
    } finally {
      setSubmitting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setSubmitting(false);
    setSubmitted(false);
    setError(null);
  }, []);

  return {
    submitLead,
    submitting,
    submitted,
    error,
    reset,
  };
}

// Default exports
export default {
  useApi,
  useFileUpload,
  useAnalysis,
  useChartData,
  useHealthCheck,
  useLeadSubmission,
};